// Marketlens React entry. Loads the Claude Design (Huddle) component modules
// verbatim - each attaches to window.Ml* - then mounts the prototype's App()
// composition. React/ReactDOM are UMD globals (index.html); the DS bundle
// (window.HuddleDesignSystem_775113) is loaded there too. Vite transpiles the
// JSX at build time against the global React (classic runtime).

// --- Marketlens section stylesheets (cascade after the DS CSS in index.html) ---
import './styles/marketlens.css';
import './styles/bi.css';
import './styles/research.css';
import './styles/seo.css';
import './styles/pipeline.css';

// App-side wiring (real backend behind the Claude Design markup)
import { api, setReadOnly, getMe, logout as apiLogout, listPanelWorkspaces, switchPanelWorkspace, getInvite, acceptInvite } from './lib/api.js';
// loadProperties rides along for the MARKET control: a property carries its
// declared markets, and docs/MODULE-SCOPE.md 1.3 makes a market a control on a
// module rather than a position of the switcher.
import { loadSites, loadProperties } from './lib/sites.js';

// --- Feature modules (side-effect imports → window.Ml*; order matters) ---
import './app/icons.jsx';
import './app/data.jsx';
import './app/shell.jsx';
import './app/screens.jsx';
import './app/content.jsx';
// Where a term's site attribution is reported. Loaded beside content.jsx
// because that is where the two screens it renders into live.
import './app/taxonomy-attribution.jsx';
import './app/editor.jsx';
import './app/bi-master.jsx';
import './app/bi-products.jsx';
import './app/bi-profile.jsx';
import './app/bi-modals.jsx';
import './app/research-analysis.jsx';
import './app/research-lenses.jsx';
import './app/research-keywords.jsx';
import './app/research-planner.jsx';
import './app/research-topics.jsx';
import './app/coverage-map.jsx';
import './app/discovery-strip.jsx';
import './app/report-drawer.jsx';
import './app/ai-drawer.jsx';
import './app/seo-dashboard.jsx';
import './app/seo-performance.jsx';
import './app/seo-audit.jsx';
import './app/seo-pages.jsx';
import './app/seo-bulkmeta.jsx';
import './app/seo-linking.jsx';
import './app/pipeline.jsx';
import './app/pipeline-inbox.jsx';
import './app/pipeline-todo.jsx';
import './app/foundations.jsx';
import './app/components-gallery.jsx';
import './app/console.jsx';

(function mount() {
  const DS = window.HuddleDesignSystem_775113;
  if (!DS || !window.React || !window.ReactDOM || !window.MlData || !window.MlShell || !window.MlScreens || !window.MlFoundations || !window.MlComponents || !window.MlContent || !window.MlEditor || !window.MlBiMaster || !window.MlBiProducts || !window.MlBiProfile || !window.MlBiModals || !window.MlResearchAnalysis || !window.MlResearchLenses || !window.MlResearchKeywords || !window.MlResearchPlanner || !window.MlResearchTopics || !window.MlCoverageMap || !window.MlDiscovery || !window.MlSeoDashboard || !window.MlSeoPerformance || !window.MlSeoAudit || !window.MlSeoPages || !window.MlSeoBulkMeta || !window.MlSeoLinking || !window.MlPipeline || !window.MlPipelineInbox || !window.MlPipelineTodo || !window.MlConsole || !window.MlAiDrawer || !window.MlIcon) {
    return void setTimeout(mount, 30);
  }
  const { useState, useEffect, useCallback } = React;
  const { MlSidebar, MlTopBar, ImpersonationBanner, Brand } = window.MlShell;
  const { AiAssistPanel } = window.MlAiDrawer;
  const { PostsScreen, GenericStub } = window.MlScreens;
  const { PagesScreen, CategoriesScreen, TagsScreen, TaxonomySuggestionsScreen, TaxonomyAuditScreen } = window.MlContent;
  const { Editor, ArticleGenerator } = window.MlEditor;
  const { BiModule, COMPETITORS, INDUSTRY, PARTNERS } = window.MlBiMaster;
  const { ProductsScreen } = window.MlBiProducts;
  const { BusinessProfile } = window.MlBiProfile;
  const BI_CFG = { "bi-competitors": COMPETITORS, "bi-industry": INDUSTRY, "bi-partners": PARTNERS };
  const { AnalysisModule } = window.MlResearchAnalysis;
  const RL = window.MlResearchLenses;
  const ANALYSIS_CFG = { "competitors-analysis": RL.COMPETITORS, "partners-analysis": RL.PARTNERS, "industry-analysis": RL.INDUSTRY, "product-enrichment": RL.PRODUCTS };
  const { KeywordsPlanner } = window.MlResearchKeywords;
  const { ContentPlanner } = window.MlResearchPlanner;
  const { TopicsBank } = window.MlResearchTopics;
  const { CoverageMap } = window.MlCoverageMap;
  const { SeoDashboard } = window.MlSeoDashboard;
  const { Performance } = window.MlSeoPerformance;
  const { SiteAudit } = window.MlSeoAudit;
  const { PageInventory, CrawlOnlyNotice } = window.MlSeoPages;
  const { BulkMeta } = window.MlSeoBulkMeta;
  const { InternalLinking } = window.MlSeoLinking;
  const { Reports, Assistant } = window.MlPipeline;
  const { Inbox } = window.MlPipelineInbox;
  const { TodoHub } = window.MlPipelineTodo;
  const { Foundations } = window.MlFoundations;
  const { Components } = window.MlComponents;
  const { ConsoleApp } = window.MlConsole;
  const { Switch } = DS;
  const { NAV_GROUPS, SITES, SCREEN_META } = window.MlData;

  // --- Location, in the hash -----------------------------------------------
  // The URL names where the user is, screen AND the thing open inside it:
  //
  //   #posts                      a screen
  //   #console/sites              a console tab
  //   #competitors-analysis/447   one entity's analysis, open
  //   #bi-competitors/447         one BI entity's detail, open
  //   #content-planner/88         one content plan, open
  //   #pages/edit/123             the editor on a saved post
  //
  // One convention, used everywhere: the screen id, then the id of the open
  // item (`edit/<postId>` is the editor's older spelling of the same idea, kept
  // because links to it are already in the wild). That makes a detail view a
  // real place: a refresh reopens exactly it, the link can be pasted to a
  // colleague, and browser Back leaves the detail for the list instead of
  // leaving the app. Before this, clicking "View analysis" on RYDGE Conseil and
  // refreshing threw you back to the list with no way to say where you had been.
  //
  // Screen changes are still written with replaceState, as they were when this
  // was refresh persistence only: walking back through every sidebar click is
  // nobody's idea of Back. OPENING an item pushes, because that is the one move
  // a user expects Back to undo. See the mirror effect in App for the rule.
  const NAV_IDS = new Set(NAV_GROUPS.flatMap(g => g.items.map(it => it.id)));
  const CONSOLE_TABS = new Set(["properties", "sites", "blogs", "markets", "reports", "users", "approvals", "settings", "billing", "pricing", "org", "docs"]);
  // Screens that can carry an open item in the hash. A whitelist rather than
  // "anything after the screen id", so a stray segment on a screen that cannot
  // act on one is ignored instead of being handed down as a detail nothing will
  // ever render, and so adding the trailing id to a screen stays a decision.
  const DETAIL_SCREENS = new Set([
    "competitors-analysis", "partners-analysis", "industry-analysis", "product-enrichment",
    "bi-competitors", "bi-industry", "bi-partners", "products",
    "content-planner",
  ]);
  // Screens that read or write a CMS. On a crawl-only site (a public website
  // with no CMS behind it) the API answers all of them CRAWL_ONLY_SITE, so
  // they are replaced here, at the router, with one honest explanation.
  //
  // At the router and NOT inside each screen, deliberately: an early return
  // added above a screen's own hooks would change its hook count the moment
  // the user switched from a WordPress site to a crawl-only one, and React
  // refuses that outright ("rendered fewer hooks than expected"). One place,
  // one message, no hook-order trap, and no screen needs to learn a second
  // kind of site exists.
  //
  // BULK META AND INTERNAL LINKING ARE NO LONGER IN THIS SET, and that is step
  // 5 resolving a contradiction MODULE-SCOPE 4.2 names at length. The nav
  // deliberately KEPT Bulk Meta for a site with no CMS, because "meta
  // write-back is where that site's SEO work belongs", and this set then
  // rendered the no-CMS notice over it: the sidebar offered the one screen
  // meant to survive having no CMS and the screen said it was unavailable.
  // Both halves were defensible alone and together they were an entry that led
  // to a refusal.
  //
  // What makes the kept entry true is a verb PER ROW rather than per screen. A
  // crawl-only section's pages are now rows in a real table, read only, with a
  // brief to hand over, and Internal Linking reads that section's pages the
  // same way. So the notice has nothing left to do on either, and it survives
  // where it is still honest: the lists and the editor, which genuinely need a
  // CMS to show a row at all.
  //
  // PAGES LEFT THIS SET ON 2026-10-20, and it is the same move Bulk Meta and
  // Internal Linking made for the same reason. The owner measured the cost of
  // it being here: "pages are 0 while we have 70 on the main site". Page
  // Inventory listed all 70 of the front end's served pages and the screen
  // called Pages said there were none, because the notice stood in for a
  // screen that only ever asked a CMS. A page with no CMS behind it still
  // appears, read only (MODULE-SCOPE's Pages row, and 3.5), so Pages now has
  // rows at a crawl-only section and the notice has nothing left to do there.
  //
  // POSTS DID NOT LEAVE, and the difference is the subject rather than the
  // screen: a post is a thing a CMS holds and nothing else holds, so a section
  // with no CMS genuinely has none, and the notice naming that reason is the
  // honest answer. The served pages it does have are under Pages.
  const CMS_SCREENS = new Set([
    "posts", "categories", "tags", "taxonomy-suggestions", "taxonomy-audit",
  ]);
  const parseLocationHash = () => {
    const parts = window.location.hash.replace(/^#\/?/, "").split("/").filter(Boolean);
    if (!parts.length) return null;
    if (parts[0] === "console") return { view: "console", consoleTab: CONSOLE_TABS.has(parts[1]) ? parts[1] : "properties" };
    if (!NAV_IDS.has(parts[0])) return null;
    const loc = { view: "app", active: parts[0] };
    if (parts[1] === "edit" && /^\d+$/.test(parts[2] || "")) loc.editId = parseInt(parts[2], 10);
    // Ids are numbers everywhere they come from (a BI entity row, a content
    // plan row), and the screens below match them with ===, so the number has
    // to come back out of the string a number.
    else if (parts[1] && DETAIL_SCREENS.has(parts[0])) loc.detailId = /^\d+$/.test(parts[1]) ? parseInt(parts[1], 10) : decodeURIComponent(parts[1]);
    return loc;
  };
  // How deep a hash points. Only used to tell "opened something" (deeper than
  // where we were) from every other move, which is what decides push vs replace.
  const hashDepth = (h) => String(h || "").replace(/^#\/?/, "").split("/").filter(Boolean).length;

  function App({ sites, reloadSites, user, onLogout, readOnly, impersonation, launchOrg }) {
    // ImyBoss SUITE workspaces (SUITE-BOARD SD-001/002/003): the suite-level
    // container shared across ImyBoss apps, NOT the MarketLens account.
    // Loaded independently of app data; a failure must not block the app
    // (the switcher just shows its "W" / "Workspace" fallback).
    const [panelWs, setPanelWs] = useState(null);
    const [panelWsList, setPanelWsList] = useState([]);
    const [wsToast, setWsToast] = useState(null);
    const wsToastT = React.useRef(null);
    const flashWs = (msg) => {
      setWsToast(msg);
      clearTimeout(wsToastT.current);
      wsToastT.current = setTimeout(() => setWsToast(null), 3200);
    };
    useEffect(() => () => clearTimeout(wsToastT.current), []);
    useEffect(() => {
      let alive = true;
      (async () => {
        try {
          const list = await listPanelWorkspaces();
          if (!alive) return;
          let pws = list;
          let active = pws.find((w) => w.current) || pws[0] || null;
          // SD-002 launch handoff: land in the suite workspace the panel
          // launched us from. boot() normally persists this before we mount (it
          // must, so scoped reads see it) and `current` already names the
          // target; this stays as the fallback for when that call didn't land.
          const target = launchOrg ? pws.find((w) => w.slug === launchOrg) : null;
          if (target && (!active || active.id !== target.id)) {
            await switchPanelWorkspace(target.id).catch(() => {});
            pws = pws.map((w) => ({ ...w, current: w.id === target.id }));
            active = target;
            if (alive) flashWs("Switched to " + target.name);
          }
          if (alive) { setPanelWsList(pws); setPanelWs(active); }
        } catch { /* switcher falls back */ }
      })();
      return () => { alive = false; };
    }, []);
    // Picking the active workspace (SD-005). A workspace IS a tenant now, so a
    // switch must be persisted server-side BEFORE anything re-renders, then the
    // page reloaded: every site, BI count and nav badge in this tree belongs to
    // the workspace we're leaving. Awaited, not fire-and-forget: landing in a
    // workspace the server didn't actually switch to is how you read one org's
    // dashboard while the switcher claims you're in another.
    const pickPanelWs = async (w) => {
      if (panelWs && panelWs.id === w.id) return;
      try {
        await switchPanelWorkspace(w.id);
      } catch {
        flashWs("Couldn't switch to " + w.name + ", still in " + (panelWs ? panelWs.name : "this workspace"));
        return;
      }
      try {
        // Entering a workspace never opens an app: the user picks a site from
        // the console. Leaving ml.siteId set would reopen the previous
        // workspace's site, which isn't even in this tenant.
        localStorage.removeItem("ml.siteId");
        sessionStorage.setItem("ml.landConsole", "1");
      } catch { /* blocked */ }
      window.location.assign("/");
    };
    // Land in the console rather than an app: on a panel launch (?org=) and on
    // the reload that follows a workspace switch. Read once per mount, and
    // consumed on read so an ordinary later reload keeps the user's own view.
    const [landConsole] = useState(() => {
      if (launchOrg) return true;
      try {
        if (sessionStorage.getItem("ml.landConsole") !== "1") return false;
        sessionStorage.removeItem("ml.landConsole");
        return true;
      } catch { return false; }
    });
    // Where the user was before this refresh, from the hash. Ignored when a
    // workspace launch forces the console landing.
    const [initialLoc] = useState(() => (landConsole ? null : parseLocationHash()));
    // A workspace with no sites can only be used through the console (connect
    // or pair a site there); the app shell has nothing to render for it.
    const noSites = !sites.length;
    // Active site survives reloads (console "Open site" would otherwise be
    // lost the moment the page refreshes back to sites[0]), but never when
    // entering a workspace: nothing is chosen there until the user clicks.
    const [siteId, setSiteId] = useState(() => {
      if (landConsole) return undefined;
      let saved = null;
      try { saved = localStorage.getItem("ml.siteId"); } catch { /* blocked */ }
      const match = saved != null && sites.find(s => String(s.id) === String(saved));
      return (match || sites[0] || {}).id;
    });
    useEffect(() => { try { if (siteId != null) localStorage.setItem("ml.siteId", String(siteId)); } catch { /* blocked */ } }, [siteId]);
    // Which screen to land on. Declared AFTER siteId on purpose: the landing
    // screen depends on which site we are landing on.
    //
    // A hash wins outright. An explicit #screen in the URL is the user's own
    // instruction, whether they typed it, bookmarked it or were sent it, and a
    // deep link that silently redirected somewhere else would be worse than no
    // deep link at all.
    //
    // With no hash to obey, the kind of site decides. A crawl-only site has no
    // CMS, so Posts there is not a list but a notice about what is unavailable:
    // a poor thing to greet someone with on the site they just added. They are
    // shown their pages instead, which is the thing MarketLens can actually
    // tell them about.
    const [active, setActive] = useState(() => {
      if (initialLoc && initialLoc.active) return initialLoc.active;
      const landing = sites.find(s => String(s.id) === String(siteId)) || sites[0];
      return landing && landing.source === "crawl" ? "page-inventory" : "posts";
    });
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [view, setView] = useState(landConsole || noSites ? "console" : (initialLoc && initialLoc.view) || "app");
    // Real impersonation comes from the session (/api/auth/me → impersonation,
    // set by the Agency Panel workspace handoff or an in-app agency session);
    // the ?as= query param remains as the prototype/demo path.
    const actingAs = new URLSearchParams(window.location.search).get("as");
    const [impersonating, setImpersonating] = useState(!!actingAs || !!impersonation);
    const stopImpersonating = async () => {
      if (!impersonation) { setImpersonating(false); return; }  // demo path
      try {
        const r = await api("/account/impersonate/stop", { method: "POST", body: "{}" });
        window.location.assign((r && r.redirect) || "/");
      } catch { window.location.assign("/"); }
    };
    const [width, setWidth] = useState(264);
    // A refresh can restore an editor only for a SAVED post (the hash carries
    // its id); an unsaved "new"/generated draft has nothing to reload from.
    const [editing, setEditing] = useState(() => (initialLoc && initialLoc.editId ? { post: { id: initialLoc.editId } } : null));
    const [genOpen, setGenOpen] = useState(false);
    const [genKw, setGenKw] = useState("");
    const [consoleTab, setConsoleTab] = useState((initialLoc && initialLoc.consoleTab) || (noSites && !landConsole ? "sites" : "properties"));
    // Which item is open inside the active screen, by id. The screens that have
    // a detail view no longer keep this to themselves: the URL is where it
    // belongs (see the convention above), so they take it as a prop and report
    // changes back here. One piece of state for all of them, because only one
    // screen is ever mounted.
    const [detailId, setDetailId] = useState(() => (initialLoc && initialLoc.detailId != null ? initialLoc.detailId : null));
    // Did opening what is currently on screen push a history entry? Only then
    // may closing it from inside the app pop that entry rather than overwrite
    // it. Overwriting leaves two consecutive entries for the same list, and the
    // first Back press after that appears to do nothing, which reads as broken.
    // False after a deep link (nothing was pushed, so Back belongs to whoever
    // sent the user here) and after any Back/Forward of their own.
    const pushedOpen = React.useRef(false);
    // Mirror the location back into the hash (see parseLocationHash above).
    useEffect(() => {
      let h = "";
      if (view === "console") h = "#console/" + consoleTab;
      else if (view === "app") {
        h = "#" + active;
        const pid = editing && editing.post && editing.post.id;
        // An unsaved "new" or generated draft has no id, so it has no URL; the
        // hash stays on the screen it was opened from.
        if (pid) h += "/edit/" + pid;
        else if (detailId != null && DETAIL_SCREENS.has(active)) h += "/" + encodeURIComponent(detailId);
      } // foundations/components: keep the last app hash; they are dev views
      const cur = window.location.hash;
      if (!h || cur === h) return;
      const url = window.location.pathname + window.location.search + h;
      // Going deeper means an item was opened: that earns its own entry so Back
      // returns to the list. So does swapping one open item for another on the
      // same screen, which is the same move twice over. Everything else
      // replaces, console tabs included: those are a view of one screen, not
      // somewhere the user went. The comparison is against the hash actually in
      // the bar rather than a previous render, so a re-render that recomputes
      // the same hash writes nothing at all and cannot stack entries.
      //
      // An EMPTY hash is never "where we were": it is the first write of a
      // fresh page load, and pushing there buries the entry the user arrived
      // on under one of ours, so their next Back lands on a URL that parses to
      // nothing and the app sits still. That one has to replace.
      const screenOf = (x) => String(x).split("/")[0];
      const push = view === "app" && hashDepth(cur) >= 1
        && (hashDepth(h) > hashDepth(cur)
          || (screenOf(h) === screenOf(cur) && hashDepth(h) > 1 && hashDepth(cur) > 1));
      try {
        if (push) { window.history.pushState({}, "", url); pushedOpen.current = true; }
        else { window.history.replaceState({}, "", url); }
      } catch { /* ignore */ }
    }, [view, active, consoleTab, editing, detailId]);
    // Back and Forward move through those hashes, so the app has to follow:
    // without this the URL would change under the user and the view would not.
    // `hashchange` rather than `popstate` because it fires only when the hash
    // really differs, which also makes a hash typed into the address bar work;
    // pushState/replaceState above never fire it, so there is no loop.
    useEffect(() => {
      const onHash = () => {
        const loc = parseLocationHash();
        // An emptied hash still means "nothing open here", so close what is
        // open rather than sitting still while the URL says otherwise. A hash
        // that is present but unreadable is left alone: guessing a screen out
        // of it would be worse than staying put.
        if (!loc) {
          if (!window.location.hash.replace(/^#\/?/, "")) { setEditing(null); setDetailId(null); pushedOpen.current = false; }
          return;
        }
        pushedOpen.current = false;
        if (loc.view === "console") { setConsoleTab(loc.consoleTab); setView("console"); return; }
        setView("app");
        setActive(loc.active);
        // Keep the editor's own state object when the id has not changed: it
        // carries the generated article and the plan/inbox links, and rebuilding
        // it from the id alone would drop them.
        setEditing(prev => (loc.editId
          ? (prev && prev.post && prev.post.id === loc.editId ? prev : { post: { id: loc.editId } })
          : null));
        setDetailId(loc.detailId != null ? loc.detailId : null);
      };
      window.addEventListener("hashchange", onHash);
      return () => window.removeEventListener("hashchange", onHash);
    }, []);
    // Closing an item from inside the app. When opening it pushed an entry,
    // closing pops it: the list the user returns to is then the SAME history
    // entry they opened from, so Back and the in-app back button agree and
    // Forward still reopens the detail. The state change arrives through the
    // hashchange above. Without a pushed entry (a deep link, or a Back of their
    // own) there is nothing to pop, so the state is cleared directly.
    const closeOpen = useCallback((clear) => {
      // The hash still has to point INSIDE a screen, or the entry being popped
      // is not the one that opened this. An unsaved generated draft has no id
      // and so no URL of its own: the editor holding it sits on a plain screen
      // hash, and going back from there would leave the screen the user is on
      // rather than close the thing in front of them.
      if (pushedOpen.current && hashDepth(window.location.hash) > 1) { pushedOpen.current = false; window.history.back(); return; }
      clear();
    }, []);
    const onDetail = useCallback((id) => {
      if (id == null) closeOpen(() => setDetailId(null));
      else setDetailId(id);
    }, [closeOpen]);
    // Open the article generator, optionally pre-filling the focus keyword
    // (e.g. "Write article" from a keyword/topic row passes its term).
    // `inboxItem` remembers which inbox item seeded this generation, so the
    // editor can mark it used and link the post once a save succeeds. The ref
    // resets on every open: a later generation from elsewhere must not claim
    // a stale inbox item.
    // The link can name an inbox item ({ id }) or a bank page ({ pageId });
    // the editor writes the matching register back once a save succeeds.
    const pendingInbox = React.useRef(null);
    const openGen = (kw, link) => { pendingInbox.current = link || null; setGenKw(typeof kw === "string" ? kw : ""); setGenOpen(true); };

    const site = sites.find(s => s.id === siteId) || sites[0];

    // The in-app switcher only moves WITHIN the active site's property (same
    // domain root). Changing property happens in the console (Properties tab).
    const propKey = (s) => (s && (s.property_root || s.url)) || "";
    const propertySites = sites.filter(s => propKey(s) === propKey(site));

    // ---- The switcher as a FILTER, which is step 5 ------------------------
    // docs/PROPERTY-DB-MIGRATION.md 5 and docs/MODULE-SCOPE.md 1.1. Until now
    // the switcher selected a database SCOPE: site_id changed and with it what
    // every screen could read, so standing on one section of a property a
    // customer could not see the whole website's data. It now selects a filter
    // over one set, and a filter always has a position where nothing is
    // filtered.
    //
    // TWO PIECES OF STATE AND THEY ARE NOT THE SAME THING, which is what keeps
    // this change small enough to be safe.
    //
    //   `siteId` is the ANCHOR CONNECTION. Every request still carries it,
    //   because enforceSiteScope authorises site_id against the session's sites
    //   and nothing else, and the property is derived from it on the server
    //   rather than passed in. So no request can name a website the session
    //   does not already hold a connection to.
    //
    //   `sectionId` is the FILTER. Null is the All position: the whole website.
    //   A number is one section, and it is the same number as that section's
    //   connection, because a row's section_id holds a site_id value (3.12b:
    //   sections are not replacing sites).
    //
    // WHY ALL IS NOT THE DEFAULT, and it is recorded as an open decision rather
    // than settled here. MODULE-SCOPE 6.2 asks whether All or the primary
    // section should be the landing position and says it needs an owner
    // decision, because it changes what every customer sees on first load. The
    // safe reading is the one that changes nothing: the switcher lands where it
    // landed before, on a section, and All is somewhere a customer goes.
    const [sectionId, setSectionId] = useState(() => {
      let saved = null;
      try { saved = localStorage.getItem("ml.sectionId"); } catch { /* blocked */ }
      if (saved === "all") return null;
      const n = saved == null ? NaN : Number(saved);
      return Number.isFinite(n) && sites.some(x => x.id === n) ? n : (site ? site.id : null);
    });
    useEffect(() => {
      try { localStorage.setItem("ml.sectionId", sectionId == null ? "all" : String(sectionId)); }
      catch { /* blocked */ }
    }, [sectionId]);
    // A section that is no longer part of the active property cannot be the
    // filter: switching property in the console would otherwise leave the
    // filter naming a section of the website the customer just left.
    useEffect(() => {
      if (sectionId != null && !propertySites.some(x => x.id === sectionId)) setSectionId(site ? site.id : null);
    }, [sectionId, propertySites, site]);

    /* ---- THE MARKET, which is a CONTROL and not a position ----------------
     *
     * docs/MODULE-SCOPE.md 1.3, settled before this was built: markets are a
     * DIMENSION and never sites (PROPERTY-MODEL.md 3.3), the switcher has
     * exactly two kinds of position (All and one section), and a market is
     * NEITHER. It is a control ON a module, reached with the switcher wherever
     * it stands. So nothing below touches `sectionId`, the switcher gains no
     * third rail, and no module gains a sidebar entry.
     *
     * WHY THE SELECTION LIVES HERE AND NOT IN EACH SCREEN. A market is a fact
     * about the WEBSITE, so Keywords and the Topics Bank answering for two
     * different markets at once would be two screens disagreeing about which
     * business the customer is looking at. One piece of state, passed down,
     * the way `sectionId` already is.
     *
     * WITH FEWER THAN TWO MARKETS THERE IS NO CONTROL AND NO SELECTION. Most
     * customers have one market and must see no new widget and send no new
     * parameter: `marketId` stays null for them, and a request with no
     * market_id gets byte for byte the answer it got before this existed. That
     * is not a nicety, it is the rule every slice of this migration has held.
     */
    const [properties, setProperties] = useState(null);   // null = not read yet | []
    useEffect(() => {
      let alive = true;
      loadProperties()
        .then(ps => { if (alive) setProperties(Array.isArray(ps) ? ps : []); })
        .catch(() => { if (alive) setProperties([]); });
      return () => { alive = false; };
    }, []);

    /* This website's declared markets, or null while nothing has been read.
     * NULL AND EMPTY ARE NOT THE SAME: "not read yet" and "this website has
     * declared no market" would render identically as an empty array, and only
     * one of them is a finding. The control draws nothing in either case, and
     * the difference matters to the effect below, which must not clear a
     * selection just because the list has not arrived. */
    const propertyMarkets = React.useMemo(() => {
      if (properties == null) return null;
      const pid = site && site.property_id != null ? Number(site.property_id) : null;
      if (pid == null || !Number.isFinite(pid)) return [];
      const hit = properties.find(p => Number(p.id) === pid);
      return hit && Array.isArray(hit.markets) ? hit.markets : [];
    }, [properties, site]);

    const [marketId, setMarketId] = useState(() => {
      let saved = null;
      try { saved = localStorage.getItem("ml.marketId"); } catch { /* blocked */ }
      const n = saved == null || saved === "" ? NaN : Number(saved);
      return Number.isFinite(n) && n > 0 ? n : null;
    });
    useEffect(() => {
      try { localStorage.setItem("ml.marketId", marketId == null ? "" : String(marketId)); }
      catch { /* blocked */ }
    }, [marketId]);

    /* The selection has to BELONG to the website on screen, and with two or
     * more markets it has to exist at all.
     *
     * `is_default` is what decides the opening market, and that IS what the
     * flag is for: it marks the market a screen opens on. It is emphatically
     * not evidence about which market an existing ROW belongs to, which is the
     * refusal lib/market-scope.js makes and which nothing here weakens: this
     * chooses what the customer is shown first, and the rows answer for
     * themselves from the market they were placed into. */
    useEffect(() => {
      if (propertyMarkets == null) return;                  // not read yet: change nothing
      if (propertyMarkets.length < 2) {
        if (marketId != null) setMarketId(null);            // one market, so no control and no parameter
        return;
      }
      if (marketId != null && propertyMarkets.some(m => Number(m.id) === marketId)) return;
      const opens = propertyMarkets.find(m => Number(m.is_default) === 1) || propertyMarkets[0];
      const id = opens && opens.id != null ? Number(opens.id) : NaN;
      setMarketId(Number.isFinite(id) && id > 0 ? id : null);
    }, [propertyMarkets, marketId]);

    /* Which section a SECTION-SCOPED screen is ANCHORED to.
       WHAT THIS USED TO BE, because the change is the point. It used to be the
       whole answer for six screens: Posts, Pages, Categories, Tags and the
       Content Planner were handed ONE section at the All position, preferring
       the anchor, and the owner reported the result in one sentence, "the whole
       website got nothing, no pages, no posts". The live property is why: its
       anchor is a crawl-only front end, so the screen a customer reached for at
       All showed a no-CMS notice about a website with a blog in it, and at best
       it showed one blog of two.

       Those five now ENUMERATE at All (MODULE-SCOPE 1.1): they take `sectionId`
       and `sections` the way the property-scoped screens already do, and they
       union over the sections' own answers with every row naming its piece. So
       this value is no longer "the section they show". It is the ANCHOR
       CONNECTION they carry, which is what authorises the request, and at a
       section it is that section, which is what keeps the single section view
       byte for byte what it was.

       Two screens still SHOW one section at All and are named in
       SECTION_SCREENS below with a banner saying which: the Taxonomy Library
       and the Taxonomy Audit. They write terms into a shared pool and judge one
       connection's posts, which is 6.1's open question, and widening them is
       not this change.

       A section that cannot publish is still passed over, for the original
       reason: a crawl-only anchor has no CMS to read, and the editor and the
       two screens above would have nothing to point at. */
    const sectionSite = sectionId != null ? site
      : (site && site.source !== "crawl" ? site : (propertySites.find(x => x.source !== "crawl") || site));
    // Switching site can land on a screen the site being switched TO does not
    // have: Posts on a crawl-only site is a notice, and Page Inventory on a
    // WordPress site is a different notice. Neither is a dead end, but a notice
    // is a poor greeting, so the screen moves with the kind of site. ONLY
    // between those two: every other screen works for both kinds, and staying
    // put is what a user expects a site switch to do.
    const pickSite = (id) => {
      const next = sites.find(s => String(s.id) === String(id));
      const nextCrawl = !!(next && next.source === "crawl");
      if (nextCrawl && CMS_SCREENS.has(active)) setActive("page-inventory");
      // The other half of this redirect is GONE: it used to send a CMS site off
      // Page Inventory and onto Posts, which was right while that screen was
      // only for a site with no CMS and is backwards now. Page Inventory is the
      // property's served lens (MODULE-SCOPE 4.5), so a customer with a blog
      // being thrown off it is the one view of the whole website being hidden
      // from the person it was built for.
      setSiteId(id);
      // Picking a section moves the FILTER with the anchor, which is what makes
      // the switcher read as one control rather than two.
      setSectionId(id);
    };
    // The All position. The anchor does not move: it is what authorises the
    // request, and changing it would make "the whole website" mean a different
    // website depending on which section you happened to be standing on.
    const pickAll = () => setSectionId(null);
    // Console → "Open site": select that site (by platform site_id) and return
    // to the app. Sites connected after boot aren't in the boot list: refresh
    // it before giving up, so a just-connected site opens immediately.
    const openSiteFromConsole = async (sid) => {
      let s = sid != null && sites.find(x => String(x.site_id) === String(sid));
      if (!s && sid != null && reloadSites) {
        const fresh = await reloadSites();
        s = fresh.find(x => String(x.site_id) === String(sid));
      }
      if (s) pickSite(s.id);
      setView("app");
    };

    // Live sidebar counts for the active site (BI entities + WP content), 0 when empty.
    const [navCounts, setNavCounts] = useState(null);
    const sid = site && (site.site_id != null ? site.site_id : site.id);
    const rawUrl = (site && (site.urlFull || site.url)) || "";
    const siteUrlFull = rawUrl ? (rawUrl.startsWith("http") ? rawUrl : "https://" + rawUrl) : null;
    const loadCounts = useCallback(() => {
      if (sid == null) { setNavCounts(null); return () => {}; }
      const sectionParam = sectionId == null ? '' : `&section_id=${sectionId}`;
      let alive = true;
      /* THE PAGES BADGE HAS TO AGREE WITH THE SCREEN IT LINKS TO. /wp/counts
         asks a CMS, and a section with no CMS has none, so the badge said 0
         over a screen that now lists the 70 pages that section serves. A count
         that disagrees with the list under it is worse than no count.
         So where the section has no CMS the badge comes from the same page
         universe the screen's rows come from: GET /crawl/summary is one COUNT
         over the stored inventory, scoped to this section exactly as Page
         Inventory scopes it, which is why the two cannot disagree either. */
      const crawlOnlyHere = sectionId != null && !!(site && site.source === 'crawl');
      Promise.all([
        api(`/bi/entities/counts?site_id=${sid}`).catch(() => ({})),
        siteUrlFull ? api(`/wp/counts?siteUrl=${encodeURIComponent(siteUrlFull)}`).catch(() => ({})) : Promise.resolve({}),
        api(`/research/todo-hub/count?site_id=${sid}`, { silent401: true }).catch(() => null),
        // The inbox badge follows the SWITCHER, because it is a count of the
        // same rows the Inbox screen lists and two figures for one question
        // disagreeing in the same window is worse than either being wrong.
        // The other three stay per connection: the to-do hub and the Topics
        // Bank are still per connection reads, so a filter they do not accept
        // would be a parameter the server ignores and a badge that lies about
        // what it counted.
        api(`/inbox/stats?site_id=${sid}${sectionParam}`, { silent401: true }).catch(() => null),
        api(`/research/topics-bank/badge?site_id=${sid}`, { silent401: true }).catch(() => null),
        crawlOnlyHere ? api(`/crawl/summary?site_id=${sid}${sectionParam}`, { silent401: true }).catch(() => null) : Promise.resolve(null),
      ]).then(([bi, content, hub, inbox, bank, served]) => {
        if (!alive) return;
        // Null and not 0 when the count could not be read: a badge showing 0
        // over a list with rows in it is the defect this exists to end, and
        // "not read" is better shown as no badge than as none.
        const servedPages = served && served.pages != null ? Number(served.pages) : null;
        setNavCounts({
          bi: bi || {},
          content: servedPages == null ? (content || {}) : { ...(content || {}), pages: servedPages },
          // Sidebar badges for the pipeline: the hub's open count, the inbox
          // items waiting on a decision (new + in review), and the Topics
          // Bank backlog (pending topics + undrafted pages + idea FAQs).
          hub: {
            todo : hub ? Number(hub.count || 0) : 0,
            inbox: inbox ? Number(inbox.new || 0) + Number(inbox.in_review || 0) : 0,
            bank : bank ? Number(bank.count || 0) : 0,
          },
        });
      });
      return () => { alive = false; };
      // `site` is in here for its SOURCE, which is what decides whether the
      // pages badge comes from a CMS or from the page universe. Without it the
      // badge would keep whichever answer it had when the switcher last moved.
    }, [sid, siteUrlFull, sectionId, site]);

    useEffect(() => loadCounts(), [loadCounts]);

    // Anything imported into or removed from a module changes a badge. api()
    // fires `data-changed` after every successful write, so the counts refresh
    // in place instead of waiting for a page reload. Debounced because a bulk
    // action (import 20 competitors) emits one event per row.
    // Saving an AI-assistance chat is the one write that changes no module's
    // contents, and it happens on every turn of a conversation: left in, each
    // message would re-read the WordPress and BI counts for nothing.
    useEffect(() => {
      let timer = null, cancelReload = null;
      const onChanged = (e) => {
        if (String(e?.detail?.path || '').startsWith('/assistant/')) return;
        clearTimeout(timer);
        timer = setTimeout(() => { if (cancelReload) cancelReload(); cancelReload = loadCounts(); }, 400);
      };
      document.addEventListener('data-changed', onChanged);
      return () => { document.removeEventListener('data-changed', onChanged); clearTimeout(timer); if (cancelReload) cancelReload(); };
    }, [loadCounts]);
    // nav item id → [counts group, key]
    const BADGE = {
      products: ["bi", "product"], "bi-competitors": ["bi", "competitor"], "bi-industry": ["bi", "industry"], "bi-partners": ["bi", "partner"],
      posts: ["content", "posts"], pages: ["content", "pages"], categories: ["content", "categories"], tags: ["content", "tags"],
      inbox: ["hub", "inbox"], todo: ["hub", "todo"], "topics-bank": ["hub", "bank"],
    };
    /* THE FOUR CONTENT BADGES CARRY NO NUMBER AT ALL, and this is not an
       omission. /wp/counts answers for ONE connection, and the four screens it
       badges now enumerate the whole website at that position. So the badge
       would be the anchor's figure sitting beside a list of three sections', in
       the same window, disagreeing: on the live property the anchor is a
       crawl-only front end, so Posts would read "0" over a list with the
       blog's posts in it. Summing the four over the sections is the other
       option and 1.1 forbids it: a total over three different CMSs is a number
       nobody asked for. The screens say per piece what each one holds, which is
       the figure that means something. */
    const NO_BADGE_AT_ALL = new Set(["posts", "pages", "categories", "tags"]);
    // A crawl-only site is a public website with no CMS behind it: MarketLens
    // reads it by crawling and can never publish to it. Two consequences for
    // the nav, both handled here so no screen has to know about the other kind.
    // A crawl-only WEBSITE and a crawl-only SECTION are different things, and
    // the gate below is about what can be WRITTEN. At a section it is that
    // section. At All it is true only when NO section of this website has a CMS,
    // because a property with one blog can publish, and a screen that refused
    // to at All would be hiding the website's own CMS behind a filter position.
    const crawlOnlySite = sectionId != null
      ? !!(site && site.source === "crawl")
      : !!(sectionSite && sectionSite.source === "crawl");
    // THE PAGE INVENTORY GATE IS RETIRED, and MODULE-SCOPE 4.5 is the argument.
    // It used to hide the screen from any site with a CMS, for a good reason at
    // the time: /crawl/pages answered NOT_CRAWL_SITE there and the user's pages
    // were under Pages instead. After 3.12 that gate is backwards. The crawl is
    // the property default, the universe is one set, and Page Inventory is the
    // screen that shows it, so hiding it on a CMS section would hide the only
    // unfiltered view of the website from the customer who has a blog, which is
    // the opposite of what the property model is for.
    //
    // The two screens stop overlapping and that is the point: PAGES IS THE CMS
    // LENS AND PAGE INVENTORY IS THE SERVED LENS. Pages shows what a connection
    // holds, drafts included, and can write. Page Inventory shows what the
    // website serves, 200s and noindex included, and cannot. They disagree on
    // purpose, and the disagreement is a finding.
    const CRAWL_ONLY_NAV = new Set();
    // The filter runs ONE way only, on purpose, and with the set above empty it
    // now hides nothing from anybody.
    //
    // It did briefly run both ways, dropping Posts, Pages, the taxonomy
    // screens, Bulk Meta and Internal Linking from a crawl-only site. That was
    // wrong twice over. Bulk Meta is where meta write-back belongs for a site
    // with no CMS, so removing it quietly deleted the path we had deliberately
    // left open. And what a site offers has to come from what it IS, not from
    // what it currently contains: the same reasoning applied to an empty blog
    // would strip a new site of every tool at the exact moment someone needs
    // them. A module decides for itself what to show when there is nothing to
    // show, which is a state, not a reason to remove the module.
    const navGroups = (navCounts
      ? NAV_GROUPS.map(g => ({ ...g, items: g.items.map(it => {
        const b = BADGE[it.id];
        if (!b) return it;
        if (sectionId == null && NO_BADGE_AT_ALL.has(it.id)) return it;
        return { ...it, badge: (navCounts[b[0]] || {})[b[1]] || 0 };
      }) }))
      : NAV_GROUPS
    ).map(g => ({ ...g, items: g.items.filter(it => crawlOnlySite || !CRAWL_ONLY_NAV.has(it.id)) }));

    // Which tab the Topics Bank opens on. Set by navigations that target a
    // specific bank (the To-do hub's "Draft page" opens Pages); cleared by
    // ordinary sidebar navigation so the bank's own default returns.
    const [bankTab, setBankTab] = useState(null);
    // Sidebar navigation closes whatever was open and starts a fresh trail: the
    // entry that held the open item has just been overwritten with this screen,
    // so there is no longer anything for a later close to pop.
    const go = (id) => { setActive(id); setView("app"); setEditing(null); setMobileOpen(false); setBankTab(null); setDetailId(null); pushedOpen.current = false; };

    // ---- AI assistance panel (topbar trigger, shell column) ----------------
    // The screen the assistant is pointed at. The editor is its own context
    // rather than the list it was opened from: "Posts" describes a table while
    // the user is looking at a draft.
    const assistModule = editing ? (active === "pages" ? "editor-page" : "editor-post") : active;
    const [assistOpen, setAssistOpen] = useState(false);
    // Moving to another screen does NOT close it. It used to, on the reasoning
    // that a conversation belonged to the screen it started on, but that put
    // the cost in the wrong place: the assistant is most useful when the work
    // crosses screens, and closing on every navigation meant reopening it and
    // losing your place to do anything that spanned two modules. The panel
    // re-points itself instead, loading the new screen's context and its
    // stored thread, so it follows the user rather than going stale. Only the
    // topbar toggle and the panel's own close button shut it now.
    /* THE EDITOR'S SCOPE IS THE SCOPE OF THE ROW THAT OPENED IT, which is
       MODULE-SCOPE 4.2 and now has to be carried rather than assumed. The lists
       enumerate at All, so a row can come from any piece of the website, and a
       post id means something only inside the CMS it came from: the editor
       handed the anchor's connection would read, save and publish against
       whatever post happens to hold that id in the wrong website. So a row
       passes ITS section and the editor is given that connection. A row with no
       section is the per connection case, where the anchor IS the row's. */
    const openEditor = (post, rowSectionId) => setEditing(post
      ? { post, sectionId: rowSectionId == null ? null : rowSectionId }
      : "new");
    const editorSite = (() => {
      const want = editing && editing !== "new" ? editing.sectionId : null;
      if (want == null) return sectionSite;
      return propertySites.find(x => x.id === want) || sectionSite;
    })();
    const openConsole = (tab) => { setConsoleTab(tab || "properties"); setView("console"); };

    // The Account console is a full-shell view (its own sidebar/topbar) -
    // render it in place of the app shell, not inside it. With no sites the
    // console is also the ONLY view: exiting it just lands back here until a
    // site is connected (the exit handler reloads the list, so a connect
    // followed by "Back to app" works in one step).
    if (view === "console" || noSites) return <ConsoleApp initialTab={consoleTab} onTab={setConsoleTab} onExit={() => { if (reloadSites) reloadSites(); setView("app"); }} onOpenSite={openSiteFromConsole} user={user} onLogout={onLogout} onFoundations={() => setView("foundations")} readOnly={readOnly} panelWs={panelWs} panelWsList={panelWsList} onPickPanelWs={pickPanelWs} />;

    /* Which screens still SHOW ONE SECTION at the All position, so the banner
       below can say which one. It used to be all six section-scoped screens and
       is now two.
       Posts, Pages, Categories, Tags and the Content Planner enumerate instead
       (MODULE-SCOPE 1.1), so a banner naming one section over them would be
       describing a list that holds three.
       The Taxonomy Library and the Taxonomy Audit stay: the Library WRITES
       terms into an install's pool on approve and the Audit judges one
       connection's posts against that pool, so widening them is 6.1's
       unsettled question rather than a filter, and a screen that shows one
       section while the switcher says the whole website has to say so. */
    const SECTION_SCREENS = new Set(["taxonomy-suggestions", "taxonomy-audit"]);
    const sectionNotice = sectionId == null && SECTION_SCREENS.has(active) && sectionSite
      ? sectionSite.name : null;

    let content;
    if (view === "foundations") {
      content = <Foundations onBack={() => setView("app")} onNav={setView} />;
    } else if (view === "components") {
      content = <Components onBack={() => setView("app")} onNav={setView} />;
    } else if (crawlOnlySite && (editing || CMS_SCREENS.has(active))) {
      // The editor is included: it is the publishing screen above all others,
      // and it is reachable by a pasted #pages/edit/123 link even when the
      // lists that normally lead to it are already showing this notice.
      content = <CrawlOnlyNotice site={sectionSite} title={editing ? "Editor" : (SCREEN_META[active] || {}).title || active} />;
    } else if (editing) {
      content = <Editor post={editing.post} gen={editing.generatedArticle} planLink={editing.planLink} inboxLink={editing.inboxLink} pageLink={editing.pageLink} onBack={() => closeOpen(() => setEditing(null))} site={editorSite} type={active === "pages" ? "page" : "post"} />;
    } else if (active === "posts") {
      content = <PostsScreen site={sectionSite} sectionId={sectionId} sections={propertySites} onEdit={openEditor} onGenerate={() => openGen()} />;
    } else if (active === "pages") {
      content = <PagesScreen site={sectionSite} sectionId={sectionId} sections={propertySites} onEdit={openEditor} />;
    } else if (active === "categories") {
      content = <CategoriesScreen site={sectionSite} sectionId={sectionId} sections={propertySites} />;
    } else if (active === "tags") {
      content = <TagsScreen site={sectionSite} sectionId={sectionId} sections={propertySites} />;
    } else if (active === "taxonomy-suggestions") {
      content = <TaxonomySuggestionsScreen site={sectionSite} />;
    } else if (active === "taxonomy-audit") {
      content = <TaxonomyAuditScreen site={sectionSite} />;
    } else if (active === "business-profile") {
      content = <BusinessProfile site={site} />;
    } else if (active === "products") {
      // The register plus its "Gaps and ideas" panel; the composition lives in
      // app/bi-products.jsx so the panel travels with the module.
      content = <ProductsScreen site={site} sites={sites} go={go} detailId={detailId} onDetail={onDetail} />;
    } else if (active === "bi-competitors" || active === "bi-industry" || active === "bi-partners") {
      // detailId/onDetail: which entity's detail is open is in the hash, so the
      // module no longer needs remounting to be pointed at one (it used to take
      // a one-shot initialEntityId, forced in with a key, for the To-do hub's
      // "Open" button; that button now just sets the location like anything else).
      content = <BiModule cfg={BI_CFG[active]} marketSet={true} state="default" site={site} sites={sites} go={go} key={active} detailId={detailId} onDetail={onDetail} />;
    } else if (active === "keywords-planner") {
      // `go` so the screen can send the user to the Taxonomy Library, which is
      // where a site without categories gets some. Without it the screen had to
      // find the sidebar link in the DOM and click it, which breaks the moment
      // the nav is restyled.
      // `sectionId` is what lets this screen offer the whole market's register.
      // Without it the prop is undefined, which the screen reads as "not told"
      // and behaves exactly as it did before, so the All position would be
      // inert on screen while the backend answered it correctly. The screen is
      // built so undefined can NEVER read as All, which is the safe direction.
      // `markets` and `marketId` are the market CONTROL: invisible with fewer
      // than two markets, so a one-market customer sees exactly this screen as
      // it was. See the block beside `propertyMarkets` for why the selection
      // lives in the shell rather than in the screen.
      content = <KeywordsPlanner onWrite={openGen} site={site} sectionId={sectionId} sections={propertySites} go={setActive}
        markets={propertyMarkets} marketId={marketId} onPickMarket={setMarketId} />;
    } else if (active === "content-planner") {
      // A PIECE of a plan is the article, so both of these are per piece.
      //
      // onDraft closes the loop: the generator answered for one piece, so the
      // editor is handed the piece along with the article and posts the link
      // back once a save has produced a real post id. Nothing else knows both
      // facts at once, which is why the link was never written before.
      content = <ContentPlanner site={sectionSite} sectionId={sectionId} sections={propertySites} detailId={detailId} onDetail={onDetail}
        onDraft={(article, notice, planLink) => {
          if (!article) return;
          setActive("posts");
          setEditing({ generatedArticle: article, planLink });
          if (notice) flashWs(notice);
        }}
        onOpenDraft={(postId, title, planSectionId) => {
          // The editor loads a post from its id alone, so the piece's
          // linked_post_id is all this needs to pass, plus the connection that
          // id is meaningful in: at the All position the plan may publish to
          // another section than the anchor.
          if (postId) { setActive("posts"); openEditor({ id: postId, title }, planSectionId); }
        }} />;
    } else if (active === "topics-bank") {
      content = <TopicsBank onWrite={openGen} site={site} go={setActive} initialTab={bankTab}
        markets={propertyMarkets} marketId={marketId} onPickMarket={setMarketId} />;
    } else if (active === "coverage-map") {
      // `go` so a site with no categories or tags can be sent to the Taxonomy
      // Library, which is where it gets some. Without a vocabulary there is no
      // axis to draw this map along, and the screen would otherwise be a dead
      // end telling the user to go somewhere it cannot take them.
      content = <CoverageMap site={site} go={setActive} />;
    } else if (active === "industry-analysis" || active === "competitors-analysis" || active === "partners-analysis" || active === "product-enrichment") {
      content = <AnalysisModule cfg={ANALYSIS_CFG[active]} listState="default" key={active} site={site} go={go} detailId={detailId} onDetail={onDetail} />;
    } else if (active === "seo-dashboard") {
      content = <SeoDashboard go={setActive} site={site} sectionId={sectionId} sections={propertySites} />;
    } else if (active === "performance") {
      content = <Performance site={site} sectionId={sectionId} sections={propertySites} />;
    } else if (active === "site-audit") {
      content = <SiteAudit state="results" site={site} sectionId={sectionId} sections={propertySites} />;
    } else if (active === "page-inventory") {
      content = <PageInventory site={site} sectionId={sectionId} sections={propertySites} />;
    } else if (active === "bulk-meta") {
      content = <BulkMeta site={site} sectionId={sectionId} sections={propertySites} />;
    } else if (active === "internal-linking") {
      content = <InternalLinking state="results" site={site} sectionId={sectionId} sections={propertySites} />;
    } else if (active === "inbox") {
      content = <Inbox onGenerate={(item) => openGen(item && item.title, item && item.id ? { id: item.id } : null)} site={site} sectionId={sectionId} sections={propertySites} />;
    } else if (active === "todo") {
      // Kind beats source label: a "complete your profile" item carries
      // source "Manual" (which maps to the hub itself), and a captured page
      // carries "Research" (keywords), so without the kind override both
      // "Open" buttons landed on the wrong screen.
      content = <TodoHub site={site} sectionId={sectionId} sections={propertySites} onNavigate={(src, kind, refId) => {
        const byKind = { profile: "business-profile", page: "topics-bank" };
        if (kind === "page") setBankTab("pages");
        if (kind && byKind[kind]) { setDetailId(null); return setActive(byKind[kind]); }
        const map = { Competitors: "bi-competitors", Partners: "bi-partners", Industry: "bi-industry", Products: "products", Inbox: "inbox", Research: "keywords-planner", Manual: "todo" };
        // An entity item opens its module ON that entity, which is now just a
        // location: the target screen plus the entity id, the same pair a click
        // inside that module would produce. Products included, since the id
        // travels in the hash rather than through a prop the screen had to
        // forward. Anything else lands on the module's list.
        const screen = map[src];
        setDetailId(kind === "entity" && refId != null && screen && DETAIL_SCREENS.has(screen) ? refId : null);
        if (screen) setActive(screen);
      }} />;
    } else if (active === "reports") {
      content = <Reports site={site} />;
    } else if (active === "ai-assistant") {
      content = <Assistant site={site} />;
    } else {
      content = <GenericStub id={active} />;
    }

    return (
      <div className={"ml-shell" + (readOnly ? " ml-readonly" : "") + (assistOpen ? " ml-shell--assist" : "")} style={{ "--ml-sidebar-width": width + "px" }}>
        {mobileOpen && <div className="ml-scrim-mobile" onClick={() => setMobileOpen(false)} />}
        <MlSidebar
          groups={navGroups} active={view === "app" ? active : null}
          onNavigate={go} collapsed={collapsed} open={mobileOpen} onToggle={() => setCollapsed(v => !v)}
          site={site} onSwitchSite={() => {}} onBrand={() => go("posts")}
          onFoundations={() => setView("foundations")} width={width} onResize={setWidth}
          user={user} onLogout={onLogout}
          onConsole={openConsole}
        />
        <div className="ml-shell__main">
          {impersonating && <ImpersonationBanner
            tenant={impersonation ? (impersonation.account_name || "client") + " · managed by " + (impersonation.actor_email || "IMYBOSS") : (actingAs ? actingAs + " · agency client" : "an agency client")}
            onExit={stopImpersonating} />}
          <MlTopBar
            site={site} sites={propertySites} onPickSite={pickSite}
            sectionId={sectionId} onPickAll={pickAll}
            propertyLabel={site && (site.property_name || site.property_root)}
            moduleId={assistModule} assistOpen={assistOpen} onToggleAssist={() => setAssistOpen(v => !v)}
            onToggleSidebar={() => setMobileOpen(v => !v)}
            user={user} onLogout={onLogout} readOnly={readOnly}
            onConsole={openConsole} onFoundations={() => setView("foundations")}
            onAddSite={() => openConsole("sites")}
          />
          {/* Never on a crawl-only site: it has no credentials to restore, so
              this banner would be an alarm about a healthy site. /wp/counts
              already 400s for one, but the guard is cheap and the false alarm
              is not. */}
          {!crawlOnlySite && navCounts?.content?.credentials === "missing" && (
            <div style={{ padding: "12px 24px 0" }}>
              <DS.Banner tone="warning" title="WordPress credentials need restoring">
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span>Showing published content from the public site. Drafts, private posts and editing stay unavailable until you reconnect.</span>
                  <DS.Button variant="secondary" onClick={() => openConsole("sites")}>Restore credentials</DS.Button>
                </div>
              </DS.Banner>
            </div>
          )}
          {sectionNotice && (
            <div style={{ padding: "12px 24px 0" }}>
              <DS.Banner tone="info" title={`Showing ${sectionNotice}`}>
                This screen is per section, so it shows one piece of the website even while the switcher is on the
                whole website. Pick a section to change which one.
              </DS.Banner>
            </div>
          )}
          <main className="ml-shell__content">{content}</main>
        </div>

        {/* A column, not a modal: the screen beside it stays live, so a
            suggestion can be acted on while it is still on screen. */}
        {assistOpen && <AiAssistPanel site={site} moduleId={assistModule} onClose={() => setAssistOpen(false)} />}

        {wsToast && <div className="ml-ws-toast" role="status">{wsToast}</div>}

        {/* The editor, the generator and the no-CMS notice take the DISPLAY
            section and not the anchor. They write to a CMS, and at All the
            anchor can be the crawl-only front end of the property while the row
            being edited came from the blog: sending a post id to the wrong
            connection is the worst defect available on these three. */}
        <ArticleGenerator open={genOpen} site={sectionSite} focusKeyword={genKw}
          onClose={() => setGenOpen(false)}
          onGenerate={(article, notice) => {
            setGenOpen(false);
            if (!article) return;
            const link = pendingInbox.current;
            setActive("posts");
            setEditing({
              generatedArticle: article,
              inboxLink: link && link.id ? link : null,
              pageLink: link && link.pageId ? link : null,
            });
            if (notice) flashWs(notice);
          }} />
      </div>
    );
  }

  // --- Wired auth gate (reuses the prototype's ml-auth design, real endpoints) ---
  // SSO cutover: ImyBoss ID (auth.imyboss.app) is the only sign-in method.
  // Auto-redirects into the OIDC flow; button as fallback (e.g. on errors).
  function AuthGate() {
    const { Button, Banner } = DS;
    const ssoError = new URLSearchParams(window.location.search).get("sso_error");
    const [redirecting, setRedirecting] = useState(false);
    const go = () => {
      setRedirecting(true);
      // SD-002: keep the panel's ?org=<slug> alive through the login redirect
      // (the backend sanitizes it, stows it in the OIDC state cookie, and
      // echoes it back after the callback).
      const org = new URLSearchParams(window.location.search).get("org");
      window.location.href = "/auth/login" + (org ? `?org=${encodeURIComponent(org)}` : "");
    };
    useEffect(() => { if (!ssoError) go(); }, []);
    return (
      <div className="ml-auth">
        <div className="ml-auth__card">
          <div className="ml-auth__head">
            <h1>Sign in to MarketLens</h1>
            <p>One ImyBoss ID for every ImyBoss app.</p>
          </div>
          <div className="ml-auth__form">
            {ssoError && <Banner tone="danger" title="Sign-in failed">Please try again.</Banner>}
            <Button variant="primary" fullWidth disabled={redirecting} onClick={go}>
              {redirecting ? "Redirecting…" : "Sign in with ImyBoss ID"}
            </Button>
            <p style={{ marginTop: 12, fontSize: 12, opacity: 0.7, textAlign: "center" }}>
              Passwords and two-factor settings moved to your ImyBoss account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // --- Invite gate: the ?invite=<token> email link lands here ---
  // Shown INSTEAD of the normal boot (AuthGate would otherwise bounce the
  // invitee straight into the SSO redirect without ever accepting). Accept is
  // passwordless: it attaches the membership, then the invitee signs in with
  // their ImyBoss ID like everyone else.
  function InviteGate({ token }) {
    const { Button, Banner, Field, Input } = DS;
    const [state, setState] = useState({ phase: "loading" });   // loading | ready | accepted | error
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    useEffect(() => {
      let alive = true;
      getInvite(token)
        .then(d => { if (alive) setState({ phase: "ready", inv: d }); })
        .catch(e => { if (alive) setState({ phase: "error", msg: (e && e.message) || "Invitation not found or expired." }); });
      return () => { alive = false; };
    }, [token]);
    // Strip ?invite and reboot normally (→ SSO sign-in or the app).
    const leave = () => window.location.assign(window.location.pathname);
    const accept = async () => {
      setBusy(true); setErr(null);
      try { await acceptInvite(token, name.trim()); setState(s => ({ ...s, phase: "accepted" })); }
      catch (e) { setErr((e && e.message) || "Could not accept the invitation."); setBusy(false); }
    };
    const inv = state.inv || {};
    return (
      <div className="ml-auth">
        <div className="ml-auth__card">
          <div className="ml-auth__head">
            <h1>Workspace invitation</h1>
            {state.phase === "ready" && <p>You{"'"}ve been invited to join <b>{inv.account_name}</b> as {inv.role}.</p>}
          </div>
          <div className="ml-auth__form">
            {state.phase === "loading" && <p style={{ fontSize: 14, textAlign: "center", opacity: 0.7 }}>Checking the invitation…</p>}
            {state.phase === "error" && <>
              <Banner tone="danger" title="Invitation not available">{state.msg} Ask for a new invite if it expired.</Banner>
              <Button variant="secondary" fullWidth onClick={leave} style={{ marginTop: 12 }}>Continue to sign in</Button>
            </>}
            {state.phase === "accepted" && <>
              <Banner tone="success" title="Invitation accepted">Sign in with your ImyBoss ID using <b>{inv.email}</b> to enter {inv.account_name}.</Banner>
              <Button variant="primary" fullWidth onClick={leave} style={{ marginTop: 12 }}>Continue to sign in</Button>
            </>}
            {state.phase === "ready" && <>
              {err && <Banner tone="danger" title="Error">{err}</Banner>}
              <p style={{ fontSize: 13, opacity: 0.75, margin: "0 0 12px" }}>This invitation is for <b>{inv.email}</b>. You{"'"}ll sign in with an ImyBoss ID on that address.</p>
              {!inv.has_account && <Field label="Your name (optional)"><Input value={name} onChange={e => setName(e.target.value)} placeholder="Shown to your teammates" /></Field>}
              <Button variant="primary" fullWidth disabled={busy} onClick={accept} style={{ marginTop: 12 }}>{busy ? "Accepting…" : "Accept invitation"}</Button>
              <Button variant="secondary" fullWidth onClick={leave} style={{ marginTop: 8 }}>Not now</Button>
            </>}
          </div>
        </div>
      </div>
    );
  }

  // --- Bootstrap: auth gate → load real sites → render the shell ---
  function Root() {
    const [phase, setPhase] = useState("loading");   // loading | login | app
    const [me, setMe] = useState(null);
    const [sites, setSites] = useState([]);
    const [launchOrg, setLaunchOrg] = useState(null);
    // ?invite=<token> (the invite email link) takes over the whole boot.
    const [inviteToken] = useState(() => {
      try { return new URLSearchParams(window.location.search).get("invite"); } catch { return null; }
    });

    const boot = useCallback(async () => {
      setPhase("loading");
      let meRes = await getMe();
      // Signed out: leave ?org= in the URL so AuthGate carries it through the
      // OIDC redirect (SUITE-BOARD SD-002).
      if (meRes.auth_required && !meRes.authenticated) {
        setMe(meRes);
        setPhase("login");
        return;
      }
      // Signed in: capture the panel's ?org=<slug> for the suite-workspace
      // match in App, then strip it so reloads/copied URLs stay clean.
      let org = null;
      try {
        const q = new URLSearchParams(window.location.search);
        org = (q.get("org") || "").trim().toLowerCase() || null;
        if (q.has("org")) {
          q.delete("org");
          const rest = q.toString();
          window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
        }
        setLaunchOrg(org);
      } catch { /* ignore */ }
      // SD-005: a launch names the tenant, so the switch must land BEFORE we
      // read anything scoped to it. An already-signed-in user never passes
      // through /auth/callback (where the org cookie is otherwise set), so
      // without this the console would list the previous org's sites and only
      // agree with the switcher after a manual reload. Re-read /me afterwards:
      // the account and role it returned came from the pre-switch scope.
      if (org) {
        try {
          const target = (await listPanelWorkspaces()).find(w => w.slug === org);
          if (target && !target.current) {
            await switchPanelWorkspace(target.id);
            meRes = await getMe();
          }
        } catch { /* no match / panel down: fall back to the stored scope */ }
      }
      setMe(meRes);
      setReadOnly(!!(meRes.role === "viewer" || (meRes.role === "member" && meRes.account && meRes.account.is_agency)));
      let s = await loadSites(meRes);
      // Mock sites are a LOCAL-DEV convenience only - never show phantom editions in
      // production. With no real sites, fall through to the "no sites" state below.
      const isLocalDev = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
      if (!s.length && isLocalDev) s = SITES;
      setSites(s);
      setPhase("app");
    }, []);

    // Re-fetch the account's sites without a full reboot: the console can
    // connect sites long after boot, and the shell must be able to see them.
    const reloadSites = useCallback(async () => {
      if (!me) return sites;
      const s = await loadSites(me);
      if (s.length) setSites(s);
      return s;
    }, [me, sites]);

    useEffect(() => { boot(); }, [boot]);
    useEffect(() => {
      const onAuthReq = () => setPhase("login");
      document.addEventListener("auth-required", onAuthReq);
      return () => document.removeEventListener("auth-required", onAuthReq);
    }, []);

    const onLogout = useCallback(async () => { try { await apiLogout(); } catch { /* */ } const u = new URL("https://auth.imyboss.app/oidc/v1/end_session"); u.searchParams.set("client_id", "380760983301344754"); u.searchParams.set("post_logout_redirect_uri", window.location.origin); window.location.assign(u.toString()); }, []);

    if (inviteToken) return <InviteGate token={inviteToken} />;
    if (phase === "loading") return <div style={{ display: "grid", placeItems: "center", height: "100vh", color: "var(--text-muted)", font: "var(--text-sm) var(--font-sans)" }}>Loading…</div>;
    if (phase === "login") return <AuthGate />;
    // No sites + signed in: App renders the console (Sites tab), where
    // connecting/pairing happens and the workspace switcher still works: an
    // empty workspace must never be a dead end. The static screen remains
    // only for the auth-off/legacy case, where there is no console to show.
    if (!sites.length && !(me && me.authenticated)) return (
      <div style={{ display: "grid", placeItems: "center", height: "100vh", textAlign: "center", padding: 24, fontFamily: "var(--font-sans)" }}>
        <div style={{ maxWidth: 440 }}>
          <div style={{ fontSize: "var(--text-lg)", fontWeight: 700, color: "var(--text-primary)", marginBottom: 8 }}>No sites connected</div>
          <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", lineHeight: 1.6 }}>This WordPress network returned no sites. Check the server's WP_NETWORK_URL and credentials.</p>
        </div>
      </div>
    );
    const readOnly = !!(me && (me.role === "viewer" || (me.role === "member" && me.account && me.account.is_agency)));
    return <App sites={sites} reloadSites={reloadSites} user={me && me.user} onLogout={onLogout} readOnly={readOnly} impersonation={me && me.impersonation} launchOrg={launchOrg} />;
  }

  ReactDOM.createRoot(document.getElementById('root')).render(<Root />);
})();
